import React, { useState } from 'react';
import { useProperty } from '../context/PropertyContext';
import toast from 'react-hot-toast';

const PropertyCard = ({ property, index }) => {
  const { formatPrice } = useProperty();
  const [isLiked, setIsLiked] = useState(false);

  const handleLike = (e) => {
    e.stopPropagation();
    setIsLiked(!isLiked);
    
    // Save to localStorage for persistence
    const likedProperties = JSON.parse(localStorage.getItem('likedProperties') || '[]');
    if (!isLiked) {
      likedProperties.push(property.id);
      toast.success('Added to favorites!');
    } else {
      const index = likedProperties.indexOf(property.id);
      if (index > -1) likedProperties.splice(index, 1);
      toast.success('Removed from favorites!');
    }
    localStorage.setItem('likedProperties', JSON.stringify(likedProperties));
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    
    const shareData = {
      title: property.title,
      text: `Check out this amazing ${property.type} in ${property.location} for ${formatPrice(property.price)}`,
      url: `${window.location.origin}#property-${property.id}`
    };

    try {
      if (navigator.share && navigator.canShare(shareData)) {
        await navigator.share(shareData);
        toast.success('Property shared successfully!');
      } else {
        // Fallback: copy to clipboard
        await navigator.clipboard.writeText(
          `${shareData.title}\n${shareData.text}\n${shareData.url}`
        );
        toast.success('Property details copied to clipboard!');
      }
    } catch (error) {
      toast.error('Unable to share property');
    }
  };

  const getBadgeClass = (badge) => {
    switch (badge.toLowerCase()) {
      case 'featured':
        return 'property-badge-luxhom featured';
      case 'new':
        return 'property-badge-luxhom new';
      case 'exclusive':
        return 'property-badge-luxhom exclusive';
      case 'just sold':
        return 'property-badge-luxhom sold';
      default:
        return 'property-badge-luxhom';
    }
  };

  // Check if property is liked on component mount
  React.useEffect(() => {
    const likedProperties = JSON.parse(localStorage.getItem('likedProperties') || '[]');
    setIsLiked(likedProperties.includes(property.id));
  }, [property.id]);

  return (
    <article className="property-card-luxhom" itemScope itemType="https://schema.org/RealEstateListing">
      <figure className="property-image-luxhom">
        <img 
          src={property.images[0]} 
          alt={`${property.title} - ${property.type} in ${property.location} - ${property.bedrooms} bedrooms, ${property.bathrooms} bathrooms, ${property.area} sq ft`}
          loading="lazy"
          itemProp="image"
        />
        
        {property.badges && property.badges.length > 0 && (
          <div className={getBadgeClass(property.badges[0])}>
            {property.badges[0]}
          </div>
        )}

        <div className="property-overlay-luxhom">
          <button 
            className={`overlay-btn-luxhom ${isLiked ? 'liked' : ''}`} 
            onClick={handleLike}
            title={isLiked ? 'Remove from favorites' : 'Add to favorites'}
            aria-label={isLiked ? 'Remove from favorites' : 'Add to favorites'}
          >
            <i className={`fa${isLiked ? 's' : 'r'} fa-heart`} aria-hidden="true"></i>
          </button>
          <button 
            className="overlay-btn-luxhom" 
            onClick={handleShare} 
            title="Share Property"
            aria-label="Share this property"
          >
            <i className="fas fa-share-alt" aria-hidden="true"></i>
          </button>
        </div>
      </figure>

      <div className="property-content-luxhom">
        <header className="property-header-luxhom">
          <div className="property-price-luxhom" itemProp="price">{formatPrice(property.price)}</div>
          <div className="property-type-luxhom" itemProp="additionalType">{property.type}</div>
        </header>

        <h3 className="property-title-luxhom" itemProp="name">{property.title}</h3>
        
        <p className="property-location-luxhom" itemProp="address">
          <i className="fas fa-map-marker-alt" aria-hidden="true"></i>
          {property.location}
        </p>

        <div className="property-features-luxhom" role="list" aria-label="Property features">
          <div className="feature-luxhom" role="listitem">
            <i className="fas fa-bed" aria-hidden="true"></i>
            <span itemProp="numberOfRooms">{property.bedrooms} Beds</span>
          </div>
          <div className="feature-luxhom" role="listitem">
            <i className="fas fa-bath" aria-hidden="true"></i>
            <span>{property.bathrooms} Baths</span>
          </div>
          <div className="feature-luxhom" role="listitem">
            <i className="fas fa-ruler-combined" aria-hidden="true"></i>
            <span itemProp="floorSize">{property.area.toLocaleString()} sq ft</span>
          </div>
          {property.garage && (
            <div className="feature-luxhom" role="listitem">
              <i className="fas fa-car" aria-hidden="true"></i>
              <span>{property.garage} Garage</span>
            </div>
          )}
        </div>

        {property.agent && (
          <div className="property-agent-luxhom" itemProp="broker" itemScope itemType="https://schema.org/RealEstateAgent">
            <img 
              src={property.agent.image} 
              alt={`${property.agent.name} - ${property.agent.title} at TrueHome Properties`}
              loading="lazy"
              itemProp="image"
            />
            <div className="agent-info-luxhom">
              <span className="agent-name-luxhom" itemProp="name">{property.agent.name}</span>
              <span className="agent-title-luxhom" itemProp="jobTitle">{property.agent.title}</span>
            </div>
            <nav className="agent-contact-luxhom" aria-label="Contact agent">
              <button 
                className="btn-contact-luxhom whatsapp"
                onClick={(e) => {
                  e.stopPropagation();
                  const message = `Hi! I'm interested in the property "${property.title}" listed on your website. Could you please provide more details?`;
                  const whatsappURL = `https://wa.me/919187458799?text=${encodeURIComponent(message)}`;
                  window.open(whatsappURL, '_blank');
                }}
                title="WhatsApp Agent"
                aria-label="Contact agent via WhatsApp"
              >
                <i className="fab fa-whatsapp" aria-hidden="true"></i>
              </button>
              <button 
                className="btn-contact-luxhom"
                onClick={(e) => {
                  e.stopPropagation();
                  window.location.href = `mailto:truehomeproperties.co.in@gmail.com?subject=Inquiry about ${property.title}`;
                }}
                title="Email Agent"
                aria-label="Email agent"
              >
                <i className="fas fa-envelope" aria-hidden="true"></i>
              </button>
            </nav>
          </div>
        )}
      </div>
    </article>
  );
};

export default PropertyCard;